// src/sectors/pilates/reports/staff/overview/ui/StaffOverviewActionListsRow.jsx
import React from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { fmtDateTR, fmtTimeTR, fmtTry, safeText } from '../utils/overviewFormatters';

function SectionCard({ title, subtitle, right, children }) {
  const theme = useTheme();
  return (
    <Card
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha(theme.palette.text.primary, 0.08),
      }}
    >
      <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <MDBox>
          <MDTypography fontWeight={950} fontSize={13}>
            {title}
          </MDTypography>
          {subtitle ? (
            <MDTypography variant="caption" sx={{ opacity: 0.72 }}>
              {subtitle}
            </MDTypography>
          ) : null}
        </MDBox>
        {right ? <MDBox>{right}</MDBox> : null}
      </MDBox>

      <Divider sx={{ my: 1.5 }} />
      {children}
    </Card>
  );
}

function RowBox({ tone = 'info', children }) {
  const theme = useTheme();
  const bg =
    tone === 'warning'
      ? alpha(theme.palette.warning.main, 0.08)
      : tone === 'error'
      ? alpha(theme.palette.error.main, 0.08)
      : alpha(theme.palette.info.main, 0.08);

  return (
    <MDBox
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha(theme.palette.text.primary, 0.08),
        bgcolor: bg,
      }}
    >
      {children}
    </MDBox>
  );
}

const EMPTY_AGG = {
  missingAttendanceSessions: [],
  expiringPlans7d: [],
  overdueDebt10d: [],
};

export function StaffOverviewActionListsRow({ trainerId, agg }) {
  const disabled = !trainerId;

  // ✅ agg undefined gelse bile patlamasın
  const safeAgg = agg ?? EMPTY_AGG;

  const missing = safeAgg.missingAttendanceSessions || [];
  const expiring = safeAgg.expiringPlans7d || [];
  const overdue = safeAgg.overdueDebt10d || [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <SectionCard
          title="Yoklaması Eksik Seanslar"
          subtitle="PENDING katılımcı olan seanslar"
          right={<Chip size="small" variant="outlined" label={`${missing.length} adet`} />}
        >
          {disabled ? (
            <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
              Eğitmen seçince listeler dolacak.
            </MDTypography>
          ) : missing.length ? (
            <MDBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {missing.map((s) => (
                <RowBox key={s.sessionId} tone="warning">
                  <MDTypography fontWeight={900} fontSize={12}>
                    {fmtDateTR(s.startAt)} • {fmtTimeTR(s.startAt)} — {safeText(s.activityName)}
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
                    {s.pendingCount} bekliyor • {s.participantsTotal} katılımcı
                  </MDTypography>
                </RowBox>
              ))}
            </MDBox>
          ) : (
            <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
              Harika 🎉 Eksik yoklama görünmüyor.
            </MDTypography>
          )}
        </SectionCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <SectionCard
          title="7 Gün İçinde Bitecek Paketler"
          subtitle="Bitiş tarihine göre"
          right={<Chip size="small" variant="outlined" label="Takip" />}
        >
          {disabled ? (
            <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
              Eğitmen seçince listeler dolacak.
            </MDTypography>
          ) : expiring.length ? (
            <MDBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {expiring.map((p) => (
                <RowBox key={p.planId} tone="info">
                  <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <MDTypography fontWeight={900} fontSize={12}>
                      {safeText(p.memberName)}
                    </MDTypography>
                    <Chip size="small" variant="outlined" label={`${p.daysLeft} gün kaldı`} />
                  </MDBox>

                  <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
                    {safeText(p.packageName)} • Bitiş: {safeText(p.endISO)}
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
                    Kullanım: {p.usedIndexMax || 0}/{p.totalSessionMax || '—'}
                  </MDTypography>
                </RowBox>
              ))}
            </MDBox>
          ) : (
            <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
              7 gün içinde biten paket yok.
            </MDTypography>
          )}
        </SectionCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <SectionCard
          title="10+ Gün Borcu Olanlar"
          subtitle="Borç yaşı (proxy: paketin ilk görünen seansı)"
          right={<Chip size="small" variant="outlined" label="Tahsilat" />}
        >
          {disabled ? (
            <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
              Eğitmen seçince listeler dolacak.
            </MDTypography>
          ) : overdue.length ? (
            <MDBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {overdue.map((p) => (
                <RowBox key={p.planId} tone="error">
                  <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <MDTypography fontWeight={900} fontSize={12}>
                      {safeText(p.memberName)}
                    </MDTypography>
                    <Chip
                      size="small"
                      color="error"
                      variant="outlined"
                      label={`${p.ageDays} gün`}
                    />
                  </MDBox>

                  <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
                    Borç: {fmtTry(p.debtTry)} • Ödenen: {fmtTry(p.paidTry)}
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
                    Paket: {safeText(p.packageName)} • Bitiş: {safeText(p.endISO)}
                  </MDTypography>
                </RowBox>
              ))}
            </MDBox>
          ) : (
            <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
              10+ gün görünen borç yok.
            </MDTypography>
          )}
        </SectionCard>
      </Grid>
    </Grid>
  );
}
